/* eslint-disable import/prefer-default-export  */
import React from 'react';
import { useHistory, useLocation } from 'react-router';
import queryString from 'querystring';
import { shallowEqual } from 'react-redux';
import { queryParams } from './utils/fetch';

export function useEventListener<
  T extends HTMLElement | Window,
  P extends Parameters<T['addEventListener']>
>(target: T, ...params: P) {
  const [name, handler, options] = params;
  React.useEffect(() => {
    // I can't call hooks conditionally, so this needs to make the effect a NOOP
    if (target == null) {
      return () => {};
    }

    try {
      target.addEventListener(name, handler, options);

      return function cleanup() {
        target.removeEventListener(name, handler);
      };
    } catch (e) {
      // embedded flows causes a cross-origin exception
      return () => {};
    }

  }, [handler, name, options, target]);
}

export function usePreviousValue<T>(value: T): undefined | T {
  const ref = React.useRef(value);
  React.useEffect(() => {
    ref.current = value;
  });

  return ref.current;
}

declare function beforeEach(f: Function): void

let htmlCounter = 1;
if (process.env.NODE_ENV === 'test' && typeof beforeEach === 'function') {
  // Each test needs to reset this so that snapshots don't change.
  beforeEach(() => {
    htmlCounter = 1;
  });
}
export function useHtmlIDs() {
  const id = React.useMemo(() => htmlCounter++, []);
  return (name: string) => `${name}_${id}`;
}


export function useRefReady(ref: React.RefObject<any>) {
  const [ready, setReady] = React.useState(
    ref.current != null
    // jsdom doesn't actually calculate positions, so there is no need to wait
    // for a 2nd render.
    || navigator.userAgent.includes('jsdom'),
  );
  React.useEffect(() => {
    if (!ready && ref.current != null) {
      setReady(true);
    }
  }, [ready, ref]);

  return ready;
}

export function useForceUpdate() {
  // The count is not used or returned. It exists only to cause the component
  // using this hook to re-render.
  const [, setCount] = React.useState(0);

  return React.useCallback(() => {
    // I just picked % 10 because I didn't want it to be able to count
    // infinitely. I think any number would work fine.
    setCount((count) => (count + 1) % 10);
  }, []);
}


type URIComponent = string | number | boolean
type QueryParamsInput = {
  // use undefined to remove an item
  [key: string]: URIComponent | URIComponent[] | undefined
}
export function useQueryParams() {
  const history = useHistory();
  const location = useLocation();
  const searchString = location.search;

  const mergeSearchParams = React.useCallback((value: QueryParamsInput, replace: boolean) => {
    // This uses history.location instead of location so that the dependencies
    // never change. history is mutable, so useCallback will generate one
    // function and keep that function forever.
    const searchParams = queryString.parse(history.location.search.substr(1));
    const newLocation = {
      ...history.location,
      search: queryParams({
        ...searchParams,
        ...value,
      }),
    };

    if (replace) {
      history.replace(newLocation);
    } else {
      history.push(newLocation);
    }
  }, [history]);

  const searchParams = React.useMemo(
    () => queryString.parse(searchString.substr(1)),
    [searchString],
  );

  const tuple: [typeof searchParams, typeof mergeSearchParams] = [searchParams, mergeSearchParams];
  return tuple;
}

export function useShallowCache<T extends object>(value: T): T {
  const refCache = React.useRef<T>(value);
  if (shallowEqual(value, refCache.current)) {
    return refCache.current;
  }
  refCache.current = value;
  return value;
}
